import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/** Fare rules result from POST /api/flights/fare-rules (on-demand). Cleared when user starts a new booking. */
export interface FareRulesResultContext {
  success: boolean;
  refund?: { allowed: boolean; amount?: number; currency?: string; description?: string; penaltyBreakdown?: unknown[] };
  exchange?: { allowed: boolean; amount?: number; currency?: string; description?: string; penaltyBreakdown?: unknown[] };
  error?: string;
}

export type TripType = "" | "oneway" | "roundtrip" | "multicity";

interface BookingContextType {
  bookingId: string;
  setBookingId: (id: string) => void;
  sessionId: string;
  setSessionId: (id: string) => void;
  revalId: string;
  setRevalId: (id: string) => void;
  /** Revalidated flight data returned by backend (safe for client). Used to survive browser refresh on PassengerDetail. */
  flightData: any | null;
  setFlightData: (v: any | null) => void;
  passengerCounts: { ADT?: number; CHD?: number; INF?: number } | null;
  setPassengerCounts: (v: { ADT?: number; CHD?: number; INF?: number } | null) => void;
  /** Fare rules for current booking; cleared on new revalidate. Fetched on demand when user opens fare-rules section. */
  fareRules: FareRulesResultContext | null;
  setFareRules: (v: FareRulesResultContext | null) => void;
  /** Trip type and dates for display/convenience (ReviewAndPayPopup, etc.). Cleared on new booking. */
  tripType: TripType;
  setTripType: (v: TripType) => void;
  departureDate: string;
  setDepartureDate: (v: string) => void;
  returnDate: string | undefined;
  setReturnDate: (v: string | undefined) => void;
  /** Segment dates for multicity (one per segment). */
  multicitySegmentDates: string[] | undefined;
  setMulticitySegmentDates: (v: string[] | undefined) => void;
}

const BookingContext = createContext<BookingContextType>({
  bookingId: "",
  setBookingId: () => {},
  sessionId: "",
  setSessionId: () => {},
  revalId: "",
  setRevalId: () => {},
  flightData: null,
  setFlightData: () => {},
  passengerCounts: null,
  setPassengerCounts: () => {},
  fareRules: null,
  setFareRules: () => {},
  tripType: "",
  setTripType: () => {},
  departureDate: "",
  setDepartureDate: () => {},
  returnDate: undefined,
  setReturnDate: () => {},
  multicitySegmentDates: undefined,
  setMulticitySegmentDates: () => {},
});

export const useBooking = () => useContext(BookingContext);

export const BookingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const storageKey = "build_travel_booking_ctx_v1";

  const initial = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      return JSON.parse(raw) as Partial<{
        bookingId: string;
        sessionId: string;
        revalId: string;
        flightData: any;
        passengerCounts: { ADT?: number; CHD?: number; INF?: number };
        tripType: TripType;
        departureDate: string;
        returnDate?: string;
        multicitySegmentDates?: string[];
      }>;
    } catch {
      return null;
    }
  }, []);

  const [bookingId, setBookingId] = useState(initial?.bookingId ?? "");
  const [sessionId, setSessionId] = useState(initial?.sessionId ?? "");
  const [revalId, setRevalId] = useState(initial?.revalId ?? "");
  const [flightData, setFlightData] = useState<any | null>(initial?.flightData ?? null);
  const [passengerCounts, setPassengerCounts] = useState<{ ADT?: number; CHD?: number; INF?: number } | null>(
    initial?.passengerCounts ?? null
  );
  const [fareRules, setFareRules] = useState<FareRulesResultContext | null>(null);
  const [tripType, setTripType] = useState<TripType>(initial?.tripType ?? "");
  const [departureDate, setDepartureDate] = useState(initial?.departureDate ?? "");
  const [returnDate, setReturnDate] = useState<string | undefined>(initial?.returnDate ?? undefined);
  const [multicitySegmentDates, setMulticitySegmentDates] = useState<string[] | undefined>(
    initial?.multicitySegmentDates ?? undefined
  );

  useEffect(() => {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          bookingId,
          sessionId,
          revalId,
          flightData,
          passengerCounts,
          tripType,
          departureDate,
          returnDate,
          multicitySegmentDates,
        })
      );
    } catch {
      // Ignore storage errors (private browsing, quota, etc.)
    }
  }, [
    bookingId,
    sessionId,
    revalId,
    flightData,
    passengerCounts,
    tripType,
    departureDate,
    returnDate,
    multicitySegmentDates,
  ]);

  return (
    <BookingContext.Provider
      value={{
        bookingId,
        setBookingId,
        sessionId,
        setSessionId,
        revalId,
        setRevalId,
        flightData,
        setFlightData,
        passengerCounts,
        setPassengerCounts,
        fareRules,
        setFareRules,
        tripType,
        setTripType,
        departureDate,
        setDepartureDate,
        returnDate,
        setReturnDate,
        multicitySegmentDates,
        setMulticitySegmentDates,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};
